import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import { Square } from '../../../../shared/components/utility/square/Square'
import React from 'react'
import { Tooltip } from '../../../../shared/components/text/tooltip/Tooltip'
import { IconCopy } from '../../../../shared/components/icons/IconCopy'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { showAlert } from '../../../../app/store/reducers/AppSlice'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import styles from './LinksList.module.scss'

export const LinksList = ({
  redirects,
  additionalRedirects,
  isLoading = false,
  className,
  type = 'default',
}: {
  redirects?: string[]
  additionalRedirects?: string[][]
  isLoading?: boolean
  className?: string
  type?: 'default' | 'simple'
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  if (isLoading) {
    return (
      <div className={clsx(styles.loader)}>
        <Preloader />
      </div>
    )
  }

  return (
    <Square className={clsx(styles.square, className)}>
      {redirects ? (
        <>
          {type === 'default' && (
            <h4 className={clsx(styles.title)}>
              {redirects.length ?? 0} {t('pages.adOverview.SERPRedirectLinks')}
            </h4>
          )}
          <ul className={clsx(styles.list)}>
            {redirects.map((link, index) => (
              <li key={link} className={clsx(styles.listItem)}>
                <span>#{index + 1}</span>
                <Tooltip overlayClassName='md:max-w-xl lg:max-w-4xl' title={link}>
                  <BpAnchor className={clsx(styles.link)} href={link} target='_blank'>
                    {link}
                  </BpAnchor>
                </Tooltip>
                <span className={clsx(styles.flex1)}>
                  <CopyToClipboard
                    text={link}
                    onCopy={() =>
                      dispatch(
                        showAlert({
                          message: t('notifications.copiedToClipboard'),
                          type: 'success',
                          timeout: 2000,
                        }),
                      )
                    }
                  >
                    <button>
                      <IconCopy
                        className={clsx(styles.copyIcon)}
                        color='#6684A7'
                      />
                    </button>
                  </CopyToClipboard>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className={clsx(styles.empty)}>
          <svg fill='none' viewBox='0 0 78 85' width='36' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M30 77.0004L35.892 80.2736C37.0264 80.904 37.5936 81.2188 38.1944 81.3424C38.726 81.452 39.274 81.452 39.806 81.3424C40.4064 81.2188 40.9736 80.904 42.108 80.2736L48 77.0004M12 67.0004L6.29188 63.8292C5.09384 63.1636 4.49472 62.8308 4.05852 62.3572C3.67264 61.9388 3.3806 61.4424 3.20192 60.9016C3 60.2904 3 59.6052 3 58.2344V52.0004M3 32.0004V25.7661C3 24.3956 3 23.7103 3.20192 23.0991C3.3806 22.5584 3.67264 22.062 4.05852 21.6433C4.49472 21.1699 5.0938 20.8371 6.29188 20.1715L12 17.0004M30 7.00032L35.892 3.72708C37.0264 3.09684 37.5936 2.78172 38.1944 2.65816C38.726 2.54884 39.274 2.54884 39.806 2.65816C40.4064 2.78172 40.9736 3.09684 42.108 3.72708L48 7.00032M66 17.0003L71.708 20.1715C72.9064 20.8371 73.5052 21.1699 73.9416 21.6433C74.3272 22.062 74.6196 22.5584 74.798 23.0991C75 23.7103 75 24.3956 75 25.7661V32.0003M75 52.0004V58.2344C75 59.6052 75 60.2904 74.798 60.9016C74.6196 61.4424 74.3272 61.9388 73.9416 62.3572C73.5052 62.8308 72.9064 63.1636 71.708 63.8292L66 67.0004M30 37.0004L39 42.0004M39 42.0004L48 37.0004M39 42.0004V52.0004M3 22.0003L12 27.0003M66 27.0003L75 22.0003M39 72.0004V82.0004'
              stroke='#6684A7'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='5'
            />
          </svg>
          <span className={clsx(styles.emptyText)}>{t('other.noResultsFound')}</span>
        </div>
      )}

      {additionalRedirects?.map((links, index) => (
        <ul key={index} className={clsx(styles.innerList)}>
          <h4 className={clsx(styles.innerTitle)}>
            {t('pages.adOverview.onSiteLinks')}
            {` #${index + 1}`}
          </h4>
          {links.map((link, innerIndex) => (
            <li key={innerIndex} className={clsx(styles.innerListItem)}>
              <span>#{innerIndex + 1}</span>
              <Tooltip title={link}>
                <BpAnchor className={clsx(styles.link)} href={link} target='_blank'>
                  {link}
                </BpAnchor>
              </Tooltip>
              <span className={clsx(styles.flex1)}>
                <CopyToClipboard
                  text={link}
                  onCopy={() =>
                    dispatch(
                      showAlert({
                        message: t('notifications.copiedToClipboard'),
                        type: 'success',
                        timeout: 2000,
                      }),
                    )
                  }
                >
                  <button>
                    <IconCopy
                      className={clsx(styles.copyIcon)}
                      color='#6684A7'
                    />
                  </button>
                </CopyToClipboard>
              </span>
            </li>
          ))}
        </ul>
      ))}
    </Square>
  )
}
